export interface StravaAthlete {
  id: number;
  firstname?: string;
  lastname?: string;
  profile?: string;
}

export interface StravaActivity {
  id: string;
  name: string;
  sportType: string;
  distanceMeters: number;
  movingTimeSeconds: number;
  elapsedTimeSeconds: number;
  totalElevationGain: number;
  startDate: string;
  startDateLocal: string;
  timezone?: string;
  averageSpeed?: number;
  maxSpeed?: number;
  summaryPolyline: string | null;
}

export interface StravaStatus {
  connected: boolean;
  athlete?: StravaAthlete;
  scope?: string;
  reason?: string;
}

type ApiErrorShape = { error?: string; message?: string };

async function readJson<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('Endpoint Strava tidak aktif. Jalankan melalui Cloudflare Pages Functions atau npm run dev:cf, bukan Vite biasa.');
  }
  const data = (await response.json()) as T & ApiErrorShape;
  if (!response.ok) throw new Error(data.error || data.message || 'Permintaan Strava gagal.');
  return data;
}

export async function getStravaStatus(): Promise<StravaStatus> {
  const response = await fetch('/api/strava/status', { credentials: 'same-origin', cache: 'no-store' });
  return readJson<StravaStatus>(response);
}

export async function startStravaConnect(): Promise<string> {
  const customEndpoint = import.meta.env.VITE_STRAVA_CONNECT_URL;
  if (customEndpoint && /^https?:\/\//.test(customEndpoint)) return customEndpoint;

  const endpoint = customEndpoint || '/api/strava/connect';
  const separator = endpoint.includes('?') ? '&' : '?';
  const response = await fetch(`${endpoint}${separator}format=json`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { accept: 'application/json' },
  });
  const data = await readJson<{ enabled: boolean; authorizeUrl?: string; message?: string }>(response);
  if (!data.enabled || !data.authorizeUrl) {
    throw new Error(data.message || 'Koneksi Strava belum dikonfigurasi.');
  }
  return data.authorizeUrl;
}

export async function getStravaActivities(): Promise<{
  athlete: StravaAthlete;
  activities: StravaActivity[];
}> {
  const response = await fetch('/api/strava/activities', {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  return readJson(response);
}

export async function disconnectStrava(): Promise<void> {
  const response = await fetch('/api/strava/disconnect', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Gagal memutuskan koneksi Strava.');
}
